/**
 * CreatePhotoChallengeModal
 * Modal for admins to create a photo challenge
 */

import React, { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  TextInput,
  ScrollView,
  Switch,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { SafeAreaView } from "react-native-safe-area-context";
import DateTimePicker from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { getAllLotties } from "@/lib/lottie-registry";

// Available Lottie animations for challenge notifications - uses central registry
const NOTIFICATION_LOTTIE_OPTIONS = getAllLotties().map((l) => ({
  id: l.key,
  name: l.name,
  file: l.source,
}));

import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { useChallenges } from "../hooks/useChallenges";
import { PhotoChallengeConfig, ChallengeVisibility, ChallengeTargetType, Challenge } from "../types";
import { Team } from "@/types";

interface AgendaEvent {
  id: string;
  title: string;
  starts_at: string;
  is_revealed: boolean;
}

interface CreatePhotoChallengeModalProps {
  visible: boolean;
  onClose: () => void;
  onCreated?: () => void;
  /** Optional: Pass a challenge to edit instead of create */
  editChallenge?: Challenge | null;
  onUpdated?: () => void;
}

/**
 * Modal component for creating/editing photo challenges
 */
export const CreatePhotoChallengeModal: React.FC<CreatePhotoChallengeModalProps> = ({
  visible,
  onClose,
  onCreated,
  editChallenge,
  onUpdated,
}) => {
  const isEditMode = !!editChallenge;
  const colors = useThemeColors();
  const { profile, activeWeekend } = useAuth();
  const { createChallenge, updateChallenge } = useChallenges();

  // Basic info
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Target
  const [targetType, setTargetType] = useState<ChallengeTargetType>("team");
  const [visibility, setVisibility] = useState<ChallengeVisibility>("all");
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

  // Teams list
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Event linking
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [linkedEventId, setLinkedEventId] = useState<string | null>(null);
  const [revealTargets, setRevealTargets] = useState<string[]>([]);

  // Android date picker mode (date first, then time)
  const [androidPickerMode, setAndroidPickerMode] = useState<"date" | "time">("date");
  const [androidPickerTarget, setAndroidPickerTarget] = useState<"from" | "until">("from");
  const [tempDate, setTempDate] = useState<Date>(new Date());

  // Points
  const [hasPoints, setHasPoints] = useState(false);
  const [points, setPoints] = useState("0");
  const [bonusPoints, setBonusPoints] = useState("0");

  // Availability
  const [hasTimeLimit, setHasTimeLimit] = useState(false);
  const [availableFrom, setAvailableFrom] = useState<Date | null>(null);
  const [availableUntil, setAvailableUntil] = useState<Date | null>(null);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState("0");

  // Photo config
  const [requirements, setRequirements] = useState<string[]>([]);
  const [newRequirement, setNewRequirement] = useState("");
  const [verificationTypeAdmin, setVerificationTypeAdmin] = useState(true);
  const [minPeople, setMinPeople] = useState("0");
  const [requireLocation, setRequireLocation] = useState(false);

  // Notification Lottie
  const [notificationLottie, setNotificationLottie] = useState("confetti");

  // Date picker state
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showUntilPicker, setShowUntilPicker] = useState(false);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Fetch teams when modal opens
   */
  useEffect(() => {
    const fetchTeams = async () => {
      if (!activeWeekend?.id) return;
      
      setTeamsLoading(true);
      try {
        const { data, error } = await supabase
          .from("teams")
          .select("*")
          .eq("weekend_id", activeWeekend.id)
          .order("name");
        
        if (error) throw error;
        setTeams(data || []);
      } catch (err) {
        console.error("Error fetching teams:", err);
      } finally {
        setTeamsLoading(false);
      }
    };

    if (visible) {
      fetchTeams();
    }
  }, [visible, activeWeekend?.id]);

  /**
   * Fetch events for linking when modal opens
   */
  useEffect(() => {
    const fetchEvents = async () => {
      if (!activeWeekend?.id) return;
      
      setEventsLoading(true);
      try {
        const { data, error } = await supabase
          .from("agenda_events")
          .select("id, title, starts_at, is_revealed")
          .eq("weekend_id", activeWeekend.id)
          .order("starts_at", { ascending: true });
        
        if (error) throw error;
        setEvents(data || []);
      } catch (err) {
        console.error("Error fetching events:", err);
      } finally {
        setEventsLoading(false);
      }
    };

    if (visible) {
      fetchEvents();
    }
  }, [visible, activeWeekend?.id]);

  /**
   * Reset form when modal opens, or populate with edit data
   */
  useEffect(() => {
    if (visible) {
      if (editChallenge) {
        // Populate form with existing challenge data
        setTitle(editChallenge.title);
        setDescription(editChallenge.description || "");
        setTargetType(editChallenge.target_type);
        setVisibility(editChallenge.visibility);
        setSelectedTeamId(editChallenge.visible_to_team_id);
        setHasPoints(editChallenge.has_points);
        setPoints(editChallenge.points?.toString() || "0");
        setBonusPoints(editChallenge.bonus_points?.toString() || "0");
        setHasTimeLimit(editChallenge.time_limit_minutes > 0);
        setAvailableFrom(editChallenge.available_from ? new Date(editChallenge.available_from) : null);
        setAvailableUntil(editChallenge.available_until ? new Date(editChallenge.available_until) : null);
        setTimeLimitMinutes(editChallenge.time_limit_minutes?.toString() || "0");
        
        // Photo config
        const config = editChallenge.config as PhotoChallengeConfig;
        setRequirements(config?.requirements || []);
        setNewRequirement("");
        setVerificationTypeAdmin(config?.verification_type === "admin");
        setMinPeople(config?.min_people?.toString() || "0");
        setRequireLocation(config?.require_location || false);
        
        // Notification
        setNotificationLottie(editChallenge.notification_lottie || "confetti");
        
        // Event linking
        setLinkedEventId(editChallenge.linked_event_id || null);
        setRevealTargets(editChallenge.reveal_targets || []);
      } else {
        // Reset form for new challenge
        setTitle("");
        setDescription("");
        setTargetType("team");
        setVisibility("all");
        setSelectedTeamId(null);
        setHasPoints(false);
        setPoints("0");
        setBonusPoints("0");
        setHasTimeLimit(false);
        setAvailableFrom(null);
        setAvailableUntil(null);
        setTimeLimitMinutes("0");
        setRequirements([]);
        setNewRequirement("");
        setVerificationTypeAdmin(true);
        setMinPeople("0");
        setRequireLocation(false);
        setLinkedEventId(null);
        setRevealTargets([]);
        setNotificationLottie("confetti");
      }
    }
  }, [visible, editChallenge]);

  /**
   * Toggle a reveal target
   */
  const toggleRevealTarget = useCallback((target: string) => {
    setRevealTargets((prev) =>
      prev.includes(target)
        ? prev.filter((t) => t !== target)
        : [...prev, target]
    );
  }, []);

  /**
   * Add a requirement to the list
   */
  const handleAddRequirement = useCallback(() => {
    if (newRequirement.trim()) {
      setRequirements((prev) => [...prev, newRequirement.trim()]);
      setNewRequirement("");
    }
  }, [newRequirement]);

  /**
   * Remove a requirement from the list
   */
  const handleRemoveRequirement = useCallback((index: number) => {
    setRequirements((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Submit the form
   */
  const handleSubmit = useCallback(async () => {
    if (!title.trim()) {
      Alert.alert("Fout", "Vul een titel in");
      return;
    }

    if (!profile?.id) {
      Alert.alert("Fout", "Je bent niet ingelogd");
      return;
    }

    setIsSubmitting(true);

    try {
      const config: PhotoChallengeConfig = {
        requirements,
        verification_type: verificationTypeAdmin ? "admin" : "auto",
        min_people: parseInt(minPeople, 10) || 0,
        require_location: requireLocation,
      };

      const challengeData = {
        weekend_id: activeWeekend?.id,
        title: title.trim(),
        description: description.trim() || undefined,
        challenge_type: "photo" as const,
        target_type: targetType,
        visibility,
        visible_to_team_id: visibility === "specific_team" ? selectedTeamId || undefined : undefined,
        has_points: hasPoints,
        points: hasPoints ? parseInt(points, 10) || 0 : 0,
        bonus_points: hasPoints ? parseInt(bonusPoints, 10) || 0 : 0,
        available_from: availableFrom?.toISOString(),
        available_until: availableUntil?.toISOString(),
        time_limit_minutes: hasTimeLimit ? parseInt(timeLimitMinutes, 10) || 0 : 0,
        config,
        linked_event_id: linkedEventId || undefined,
        reveal_targets: linkedEventId ? revealTargets : [],
        notification_lottie: notificationLottie,
      };

      if (isEditMode && editChallenge) {
        // Update existing challenge
        const success = await updateChallenge(editChallenge.id, challengeData);

        if (success) {
          Alert.alert(
            "Succes",
            "Foto opdracht bijgewerkt!",
            [
              {
                text: "OK",
                onPress: () => {
                  onUpdated?.();
                  onClose();
                },
              },
            ]
          );
        }
      } else {
        // Create new challenge
        const challenge = await createChallenge({
          ...challengeData,
          status: "draft", // Start as draft
        });

        if (challenge) {
          Alert.alert(
            "Succes",
            "Foto opdracht aangemaakt! Je kunt deze nu activeren of koppelen aan een evenement.",
            [
              {
                text: "OK",
                onPress: () => {
                  onCreated?.();
                  onClose();
                },
              },
            ]
          );
        }
      }
    } catch (err: any) {
      console.error("Error creating photo challenge:", err);
      Alert.alert("Fout", err.message || "Kon uitdaging niet aanmaken");
    } finally {
      setIsSubmitting(false);
    }
  }, [
    title,
    description,
    targetType,
    visibility,
    hasPoints,
    points,
    bonusPoints,
    hasTimeLimit,
    availableFrom,
    availableUntil,
    timeLimitMinutes,
    requirements,
    verificationTypeAdmin,
    minPeople,
    requireLocation,
    profile?.id,
    activeWeekend?.id,
    createChallenge,
    onCreated,
    onClose,
  ]);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top"]}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        className="flex-1"
      >
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 py-3 border-b"
          style={{ borderColor: colors.border }}
        >
          <TouchableOpacity onPress={onClose} disabled={isSubmitting}>
            <Text style={{ color: colors.primary }}>Annuleren</Text>
          </TouchableOpacity>
          <Text className="font-semibold text-lg" style={{ color: colors.text }}>
            {isEditMode ? "Foto opdracht bewerken" : "Foto opdracht"}
          </Text>
          <TouchableOpacity onPress={handleSubmit} disabled={isSubmitting}>
            <Text className="font-semibold" style={{ color: isSubmitting ? colors.textTertiary : colors.primary }}>
              {isSubmitting ? "Bezig..." : isEditMode ? "Opslaan" : "Aanmaken"}
            </Text>
          </TouchableOpacity>
        </View>

        <ScrollView className="flex-1 px-4 pt-4" showsVerticalScrollIndicator={false}>
          {/* Title */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Titel *
            </Text>
            <TextInput
              value={title}
              onChangeText={setTitle}
              placeholder="Bijv. 'Maak een grappige teamfoto'"
              placeholderTextColor={colors.textTertiary}
              className="px-4 py-3 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
            />
          </View>

          {/* Description */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Beschrijving
            </Text>
            <TextInput
              value={description}
              onChangeText={setDescription}
              placeholder="Geef een uitgebreide beschrijving van de opdracht..."
              placeholderTextColor={colors.textTertiary}
              className="px-4 py-3 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary, color: colors.text, minHeight: 80 }}
              multiline
              numberOfLines={3}
            />
          </View>

          {/* Target Type */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Voor wie?
            </Text>
            <View className="flex-row">
              <TouchableOpacity
                onPress={() => setTargetType("team")}
                className="flex-1 mr-2 px-4 py-3 rounded-xl flex-row items-center justify-center"
                style={{
                  backgroundColor: targetType === "team" ? colors.primary : colors.surfaceSecondary,
                }}
              >
                <Ionicons
                  name="people"
                  size={20}
                  color={targetType === "team" ? "#FFFFFF" : colors.textSecondary}
                />
                <Text
                  className="ml-2 font-medium"
                  style={{ color: targetType === "team" ? "#FFFFFF" : colors.text }}
                >
                  Team
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setTargetType("individual")}
                className="flex-1 ml-2 px-4 py-3 rounded-xl flex-row items-center justify-center"
                style={{
                  backgroundColor: targetType === "individual" ? colors.primary : colors.surfaceSecondary,
                }}
              >
                <Ionicons
                  name="person"
                  size={20}
                  color={targetType === "individual" ? "#FFFFFF" : colors.textSecondary}
                />
                <Text
                  className="ml-2 font-medium"
                  style={{ color: targetType === "individual" ? "#FFFFFF" : colors.text }}
                >
                  Individueel
                </Text>
              </TouchableOpacity>
            </View>
            <Text className="text-xs mt-2" style={{ color: colors.textTertiary }}>
              {targetType === "team"
                ? "Elk teamlid kan de opdracht voltooien voor het hele team"
                : "Elke gebruiker moet de opdracht individueel voltooien"}
            </Text>
          </View>

          {/* Visibility */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Zichtbaarheid
            </Text>
            <View className="flex-row flex-wrap">
              {[
                { value: "all", label: "Alle teams", icon: "globe" },
                { value: "specific_team", label: "Specifiek team", icon: "people-circle" },
              ].map((option) => (
                <TouchableOpacity
                  key={option.value}
                  onPress={() => {
                    setVisibility(option.value as ChallengeVisibility);
                    if (option.value === "all") setSelectedTeamId(null);
                  }}
                  className="mr-2 mb-2 px-4 py-2 rounded-full flex-row items-center"
                  style={{
                    backgroundColor: visibility === option.value ? colors.primary : colors.surfaceSecondary,
                  }}
                >
                  <Ionicons
                    name={option.icon as any}
                    size={16}
                    color={visibility === option.value ? "#FFFFFF" : colors.textSecondary}
                  />
                  <Text
                    className="ml-2 text-sm font-medium"
                    style={{ color: visibility === option.value ? "#FFFFFF" : colors.text }}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Team Selector (when specific team is selected) */}
          {visibility === "specific_team" && (
            <View className="mb-4">
              <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                Selecteer team
              </Text>
              {teamsLoading ? (
                <View className="py-4 items-center">
                  <ActivityIndicator size="small" color={colors.primary} />
                </View>
              ) : teams.length === 0 ? (
                <View
                  className="p-4 rounded-xl items-center"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Text style={{ color: colors.textSecondary }}>Geen teams beschikbaar</Text>
                </View>
              ) : (
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {teams.map((team) => (
                    <TouchableOpacity
                      key={team.id}
                      onPress={() => setSelectedTeamId(team.id)}
                      className="mr-2 px-4 py-3 rounded-xl flex-row items-center"
                      style={{
                        backgroundColor: selectedTeamId === team.id ? colors.primary : colors.surfaceSecondary,
                        borderWidth: selectedTeamId === team.id ? 0 : 1,
                        borderColor: colors.border,
                      }}
                    >
                      <View
                        className="w-8 h-8 rounded-full items-center justify-center mr-2"
                        style={{ backgroundColor: team.color || colors.primary }}
                      >
                        <Text className="font-bold text-white text-xs">
                          {team.name.substring(0, 2).toUpperCase()}
                        </Text>
                      </View>
                      <Text
                        className="font-medium"
                        style={{ color: selectedTeamId === team.id ? "#FFFFFF" : colors.text }}
                      >
                        {team.name}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              )}
            </View>
          )}

          {/* Divider */}
          <View className="h-px my-4" style={{ backgroundColor: colors.border }} />

          {/* Points */}
          <View
            className="flex-row items-center justify-between mb-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="star" size={20} color={colors.warning} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Punten toekennen
              </Text>
            </View>
            <Switch
              value={hasPoints}
              onValueChange={setHasPoints}
              trackColor={{ false: "#4B5563", true: colors.primary }}
              thumbColor={hasPoints ? "#FFFFFF" : "#9CA3AF"}
              ios_backgroundColor="#4B5563"
            />
          </View>

          {hasPoints && (
            <View className="mb-4 flex-row">
              <View className="flex-1 mr-2">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Punten
                </Text>
                <TextInput
                  value={points}
                  onChangeText={setPoints}
                  placeholder="0"
                  keyboardType="number-pad"
                  placeholderTextColor={colors.textTertiary}
                  className="px-4 py-3 rounded-xl text-center"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                />
              </View>
              <View className="flex-1 ml-2">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Bonus (snelste)
                </Text>
                <TextInput
                  value={bonusPoints}
                  onChangeText={setBonusPoints}
                  placeholder="0"
                  keyboardType="number-pad"
                  placeholderTextColor={colors.textTertiary}
                  className="px-4 py-3 rounded-xl text-center"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                />
              </View>
            </View>
          )}

          {/* Divider */}
          <View className="h-px my-4" style={{ backgroundColor: colors.border }} />

          {/* Photo Requirements */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Vereisten voor de foto
            </Text>
            
            {/* Requirements list */}
            {requirements.map((req, index) => (
              <View
                key={index}
                className="flex-row items-center mb-2 px-4 py-2 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="checkmark-circle" size={18} color={colors.success} />
                <Text className="flex-1 ml-2" style={{ color: colors.text }}>
                  {req}
                </Text>
                <TouchableOpacity onPress={() => handleRemoveRequirement(index)}>
                  <Ionicons name="close-circle" size={20} color={colors.error} />
                </TouchableOpacity>
              </View>
            ))}

            {/* Add requirement input */}
            <View className="flex-row items-center">
              <TextInput
                value={newRequirement}
                onChangeText={setNewRequirement}
                placeholder="Bijv. 'Minimaal 3 teamleden zichtbaar'"
                placeholderTextColor={colors.textTertiary}
                className="flex-1 px-4 py-3 rounded-xl"
                style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                onSubmitEditing={handleAddRequirement}
              />
              <TouchableOpacity
                onPress={handleAddRequirement}
                className="ml-2 w-10 h-10 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary }}
              >
                <Ionicons name="add" size={24} color="#FFFFFF" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Verification type */}
          <View
            className="flex-row items-center justify-between mb-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center flex-1">
              <Ionicons name="shield-checkmark" size={20} color={colors.primary} />
              <View className="ml-3 flex-1">
                <Text className="font-medium" style={{ color: colors.text }}>
                  Admin goedkeuring
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  {verificationTypeAdmin
                    ? "Admin moet foto handmatig goedkeuren"
                    : "Foto wordt automatisch goedgekeurd"}
                </Text>
              </View>
            </View>
            <Switch
              value={verificationTypeAdmin}
              onValueChange={setVerificationTypeAdmin}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          {/* Min people */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Minimum aantal personen op foto
            </Text>
            <TextInput
              value={minPeople}
              onChangeText={setMinPeople}
              placeholder="0 (geen minimum)"
              keyboardType="number-pad"
              placeholderTextColor={colors.textTertiary}
              className="px-4 py-3 rounded-xl"
              style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
            />
          </View>

          {/* Require location */}
          <View
            className="flex-row items-center justify-between mb-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="location" size={20} color={colors.primary} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Locatie vereist
              </Text>
            </View>
            <Switch
              value={requireLocation}
              onValueChange={setRequireLocation}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          {/* Divider */}
          <View className="h-px my-4" style={{ backgroundColor: colors.border }} />

          {/* Event Linking */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Koppelen aan evenement (optioneel)
            </Text>
            <Text className="text-xs mb-3" style={{ color: colors.textTertiary }}>
              Als je deze uitdaging koppelt aan een evenement, wordt dat evenement onthuld wanneer de uitdaging is voltooid.
            </Text>
            
            {eventsLoading ? (
              <View className="py-4 items-center">
                <ActivityIndicator size="small" color={colors.primary} />
              </View>
            ) : events.length === 0 ? (
              <View
                className="p-4 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Text style={{ color: colors.textSecondary }}>Geen evenementen beschikbaar</Text>
              </View>
            ) : (
              <>
                <ScrollView 
                  horizontal 
                  showsHorizontalScrollIndicator={false}
                  className="mb-3"
                >
                  <TouchableOpacity
                    onPress={() => {
                      setLinkedEventId(null);
                      setRevealTargets([]);
                    }}
                    className="mr-2 px-4 py-3 rounded-xl"
                    style={{
                      backgroundColor: !linkedEventId ? colors.primary : colors.surfaceSecondary,
                      borderWidth: !linkedEventId ? 0 : 1,
                      borderColor: colors.border,
                    }}
                  >
                    <Text
                      className="font-medium"
                      style={{ color: !linkedEventId ? "#FFFFFF" : colors.text }}
                    >
                      Geen koppeling
                    </Text>
                  </TouchableOpacity>
                  {events.map((event) => (
                    <TouchableOpacity
                      key={event.id}
                      onPress={() => setLinkedEventId(event.id)}
                      className="mr-2 px-4 py-3 rounded-xl"
                      style={{
                        backgroundColor: linkedEventId === event.id ? colors.primary : colors.surfaceSecondary,
                        borderWidth: linkedEventId === event.id ? 0 : 1,
                        borderColor: colors.border,
                      }}
                    >
                      <Text
                        className="font-medium"
                        style={{ color: linkedEventId === event.id ? "#FFFFFF" : colors.text }}
                        numberOfLines={1}
                      >
                        {event.title}
                      </Text>
                      <Text
                        className="text-xs mt-0.5"
                        style={{ color: linkedEventId === event.id ? "rgba(255,255,255,0.7)" : colors.textTertiary }}
                      >
                        {format(new Date(event.starts_at), "d MMM", { locale: nl })}
                        {event.is_revealed ? "" : " • 🔒"}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>

                {/* Reveal targets */}
                {linkedEventId && (
                  <View
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Text className="font-medium mb-3" style={{ color: colors.text }}>
                      Wat moet onthuld worden?
                    </Text>
                    <View className="flex-row flex-wrap">
                      {[
                        { value: "title", label: "Titel", icon: "text" },
                        { value: "location", label: "Locatie", icon: "location" },
                        { value: "description", label: "Beschrijving", icon: "document-text" },
                      ].map((target) => (
                        <TouchableOpacity
                          key={target.value}
                          onPress={() => toggleRevealTarget(target.value)}
                          className="mr-2 mb-2 px-3 py-2 rounded-lg flex-row items-center"
                          style={{
                            backgroundColor: revealTargets.includes(target.value)
                              ? colors.primary
                              : colors.surface,
                            borderWidth: 1,
                            borderColor: revealTargets.includes(target.value)
                              ? colors.primary
                              : colors.border,
                          }}
                        >
                          <Ionicons
                            name={target.icon as any}
                            size={16}
                            color={revealTargets.includes(target.value) ? "#FFFFFF" : colors.textSecondary}
                          />
                          <Text
                            className="ml-2 text-sm font-medium"
                            style={{
                              color: revealTargets.includes(target.value) ? "#FFFFFF" : colors.text,
                            }}
                          >
                            {target.label}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}
              </>
            )}
          </View>

          {/* Divider */}
          <View className="h-px my-4" style={{ backgroundColor: colors.border }} />

          {/* Time availability */}
          <View
            className="flex-row items-center justify-between mb-4 px-4 py-3 rounded-xl"
            style={{ backgroundColor: colors.surfaceSecondary }}
          >
            <View className="flex-row items-center">
              <Ionicons name="time" size={20} color={colors.primary} />
              <Text className="ml-3 font-medium" style={{ color: colors.text }}>
                Beschikbaarheid instellen
              </Text>
            </View>
            <Switch
              value={hasTimeLimit}
              onValueChange={setHasTimeLimit}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor="#FFFFFF"
            />
          </View>

          {hasTimeLimit && (
            <>
              {/* Available from */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Beschikbaar vanaf
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    setAndroidPickerTarget("from");
                    setAndroidPickerMode("date");
                    setTempDate(availableFrom || new Date());
                    setShowFromPicker(true);
                  }}
                  className="flex-row items-center px-4 py-3 rounded-xl"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="calendar" size={20} color={colors.primary} />
                  <Text className="ml-3 flex-1" style={{ color: availableFrom ? colors.text : colors.textTertiary }}>
                    {availableFrom
                      ? format(availableFrom, "d MMM yyyy HH:mm", { locale: nl })
                      : "Geen startdatum"}
                  </Text>
                  {availableFrom && (
                    <TouchableOpacity onPress={() => setAvailableFrom(null)}>
                      <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                    </TouchableOpacity>
                  )}
                </TouchableOpacity>
              </View>

              {/* Available until */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Beschikbaar tot
                </Text>
                <TouchableOpacity
                  onPress={() => {
                    setAndroidPickerTarget("until");
                    setAndroidPickerMode("date");
                    setTempDate(availableUntil || new Date());
                    setShowUntilPicker(true);
                  }}
                  className="flex-row items-center px-4 py-3 rounded-xl"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <Ionicons name="calendar" size={20} color={colors.primary} />
                  <Text className="ml-3 flex-1" style={{ color: availableUntil ? colors.text : colors.textTertiary }}>
                    {availableUntil
                      ? format(availableUntil, "d MMM yyyy HH:mm", { locale: nl })
                      : "Geen einddatum"}
                  </Text>
                  {availableUntil && (
                    <TouchableOpacity onPress={() => setAvailableUntil(null)}>
                      <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                    </TouchableOpacity>
                  )}
                </TouchableOpacity>
              </View>

              {/* Time limit */}
              <View className="mb-4">
                <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
                  Tijdslimiet (minuten na starten)
                </Text>
                <TextInput
                  value={timeLimitMinutes}
                  onChangeText={setTimeLimitMinutes}
                  placeholder="0 (geen limiet)"
                  keyboardType="number-pad"
                  placeholderTextColor={colors.textTertiary}
                  className="px-4 py-3 rounded-xl"
                  style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
                />
              </View>
            </>
          )}

          {/* Notification Lottie */}
          <View className="mb-4">
            <Text className="text-sm font-medium mb-2" style={{ color: colors.textSecondary }}>
              Notificatie animatie
            </Text>
            <Text className="text-xs mb-3" style={{ color: colors.textTertiary }}>
              Deze animatie wordt getoond wanneer de uitdaging beschikbaar komt
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {NOTIFICATION_LOTTIE_OPTIONS.map((option) => (
                <TouchableOpacity
                  key={option.id}
                  onPress={() => setNotificationLottie(option.id)}
                  className="items-center mr-3"
                >
                  <View
                    className="w-16 h-16 rounded-xl items-center justify-center"
                    style={{
                      backgroundColor: colors.surfaceSecondary,
                      borderWidth: notificationLottie === option.id ? 2 : 1,
                      borderColor: notificationLottie === option.id ? colors.primary : colors.border,
                    }}
                  >
                    <LottieView
                      source={option.file}
                      autoPlay
                      loop
                      style={{ width: 40, height: 40 }}
                    />
                  </View>
                  <Text
                    className="text-xs mt-1 text-center"
                    style={{ 
                      color: notificationLottie === option.id ? colors.primary : colors.textSecondary,
                      fontWeight: notificationLottie === option.id ? "600" : "400",
                    }}
                  >
                    {option.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Bottom padding */}
          <View className="h-8" />
        </ScrollView>

        {/* Date Pickers */}
        {Platform.OS === "ios" && showFromPicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View className="rounded-t-2xl p-4" style={{ backgroundColor: colors.surface }}>
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowFromPicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleren</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowFromPicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>
                      Klaar
                    </Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={availableFrom || new Date()}
                  mode="datetime"
                  display="spinner"
                  onChange={(event, date) => {
                    if (date) setAvailableFrom(date);
                  }}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {Platform.OS === "ios" && showUntilPicker && (
          <Modal transparent animationType="fade">
            <View className="flex-1 justify-end" style={{ backgroundColor: colors.overlay }}>
              <View className="rounded-t-2xl p-4" style={{ backgroundColor: colors.surface }}>
                <View className="flex-row justify-between mb-4">
                  <TouchableOpacity onPress={() => setShowUntilPicker(false)}>
                    <Text style={{ color: colors.primary }}>Annuleren</Text>
                  </TouchableOpacity>
                  <TouchableOpacity onPress={() => setShowUntilPicker(false)}>
                    <Text className="font-semibold" style={{ color: colors.primary }}>
                      Klaar
                    </Text>
                  </TouchableOpacity>
                </View>
                <DateTimePicker
                  value={availableUntil || new Date()}
                  mode="datetime"
                  display="spinner"
                  onChange={(event, date) => {
                    if (date) setAvailableUntil(date);
                  }}
                  minimumDate={availableFrom || undefined}
                  locale="nl-NL"
                  is24Hour={true}
                />
              </View>
            </View>
          </Modal>
        )}

        {/* Android date pickers - two-step process (date then time) */}
        {Platform.OS === "android" && (showFromPicker || showUntilPicker) && (
          <DateTimePicker
            value={tempDate}
            mode={androidPickerMode}
            display="default"
            onChange={(event, date) => {
              if (event.type === "dismissed") {
                setShowFromPicker(false);
                setShowUntilPicker(false);
                setAndroidPickerMode("date");
                return;
              }
              
              if (date) {
                if (androidPickerMode === "date") {
                  // Date selected, now show time picker
                  setTempDate(date);
                  setAndroidPickerMode("time");
                } else {
                  // Time selected, save the final date
                  const finalDate = new Date(tempDate);
                  finalDate.setHours(date.getHours(), date.getMinutes());
                  
                  if (androidPickerTarget === "from") {
                    setAvailableFrom(finalDate);
                  } else {
                    setAvailableUntil(finalDate);
                  }
                  
                  setShowFromPicker(false);
                  setShowUntilPicker(false);
                  setAndroidPickerMode("date");
                }
              }
            }}
            minimumDate={androidPickerTarget === "until" && availableFrom ? availableFrom : undefined}
            is24Hour={true}
          />
        )}
      </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );
};

export default CreatePhotoChallengeModal;

